import api from './api';

/**
 * E-Commerce Service
 * Handles all API calls for the e-commerce module
 * Base URL: /api/ecommerce/
 */

const ecommerceService = {
  // ==================== AUTHENTICATION ====================

  /**
   * Register a new customer
   */
  registerCustomer: (customerData) => {
    return api.post('/api/ecommerce/auth/register/', customerData);
  },

  /**
   * Login customer
   */
  loginCustomer: (credentials) => {
    return api.post('/api/ecommerce/auth/login/', credentials);
  },

  /**
   * Logout customer
   */
  logoutCustomer: (refreshToken) => {
    return api.post('/api/ecommerce/auth/logout/', { refresh: refreshToken });
  },

  // ==================== PRODUCTS ====================

  /**
   * Get all products with optional filters
   * @param {Object} params - Query parameters (category, department, search, etc.)
   */
  getProducts: (params = {}) => {
    return api.get('/api/ecommerce/products/', { params });
  },

  /**
   * Get product details by ID
   * @param {number} productId - Product ID
   */
  getProduct: (productId) => {
    return api.get(`/api/ecommerce/products/${productId}/`);
  },

  // ==================== STOREFRONT CONFIG ====================

  /**
   * Get the consolidated public storefront configuration
   * (settings/branding, banners, homepage sections, featured categories,
   * product features and navigation) in a single request.
   */
  getStorefrontConfig: (params = {}) => {
    return api.get('/api/ecommerce/storefront-config/', { params });
  },

  /**
   * Get featured products
   */
  getFeaturedProducts: () => {
    return api.get('/api/ecommerce/products/featured/');
  },

  /**
   * Get new arrival products
   */
  getNewArrivals: () => {
    return api.get('/api/ecommerce/products/new-arrivals/');
  },

  /**
   * Get best seller products
   */
  getBestSellers: () => {
    return api.get('/api/ecommerce/products/best-sellers/');
  },

  /**
   * Get products on sale
   */
  getOnSaleProducts: () => {
    return api.get('/api/ecommerce/products/on-sale/');
  },

  /**
   * Search products
   * @param {string} query - Search query
   * @param {boolean} autocomplete - Enable autocomplete mode
   */
  searchProducts: (query, autocomplete = false) => {
    return api.get('/api/ecommerce/products/search/', {
      params: { q: query, autocomplete },
    });
  },

  /**
   * Get related products
   * @param {number} productId - Product ID
   */
  getRelatedProducts: (productId) => {
    return api.get(`/api/ecommerce/products/${productId}/related/`);
  },

  // ==================== CATEGORIES ====================

  /**
   * Get all categories
   * @param {Object} params - Query parameters (department, parent, etc.)
   */
  getCategories: (params = {}) => {
    return api.get('/api/ecommerce/categories/', { params });
  },

  /**
   * Get category details
   * @param {number} categoryId - Category ID
   */
  getCategory: (categoryId) => {
    return api.get(`/api/ecommerce/categories/${categoryId}/`);
  },

  // ==================== CART ====================

  /**
   * Get or create cart for current session/user
   */
  getCart: () => {
    return api.get('/api/ecommerce/cart/');
  },

  /**
   * Add item to cart
   * @param {number} variantId - Product variant ID
   * @param {number} quantity - Quantity to add
   */
  addToCart: (productId, variantId, quantity) => {
    return api.post('/api/ecommerce/cart/add_item/', {
      web_product_id: productId,
      variant_id: variantId || null,
      quantity,
    });
  },

  /**
   * Update cart item quantity
   * @param {number} itemId - Cart item ID
   * @param {number} quantity - New quantity
   */
  updateCartItem: (itemId, quantity) => {
    return api.post('/api/ecommerce/cart/update_item/', {
      item_id: itemId,
      quantity,
    });
  },

  /**
   * Remove item from cart
   * @param {number} itemId - Cart item ID
   */
  removeFromCart: (itemId) => {
    return api.post('/api/ecommerce/cart/remove_item/', {
      item_id: itemId,
    });
  },

  /**
   * Apply coupon code to cart
   * @param {string} couponCode - Coupon code
   */
  applyCoupon: (couponCode) => {
    return api.post('/api/ecommerce/cart/apply_coupon/', {
      coupon_code: couponCode,
    });
  },

  /**
   * Remove applied coupon from cart
   */
  removeCoupon: () => {
    return api.post('/api/ecommerce/cart/remove_coupon/');
  },

  /**
   * Clear all items from cart
   */
  clearCart: () => {
    return api.post('/api/ecommerce/cart/clear/');
  },

  // ==================== CHECKOUT ====================

  /**
   * Validate checkout data
   * @param {Object} checkoutData - Checkout validation data
   */
  validateCheckout: (checkoutData) => {
    return api.post('/api/ecommerce/checkout/validate/', checkoutData);
  },

  /**
   * Get available shipping methods
   * @param {Object} params - Shipping location params
   */
  getShippingMethods: (params = {}) => {
    return api.get('/api/ecommerce/checkout/shipping-methods/', { params });
  },

  /**
   * Get available payment methods
   */
  getPaymentMethods: () => {
    return api.get('/api/ecommerce/checkout/payment-methods/');
  },

  /**
   * Create order from cart
   * @param {Object} orderData - Order creation data
   */
  createOrder: (orderData) => {
    return api.post('/api/ecommerce/checkout/create-order/', orderData);
  },

  // ==================== ORDERS ====================

  /**
   * Get customer orders
   * @param {Object} params - Query parameters (status, ordering, etc.)
   */
  getOrders: (params = {}) => {
    return api.get('/api/ecommerce/orders/', { params });
  },

  /**
   * Get order details
   * @param {number} orderId - Order ID
   */
  getOrder: (orderId) => {
    return api.get(`/api/ecommerce/orders/${orderId}/`);
  },

  /**
   * Cancel order
   * @param {number} orderId - Order ID
   * @param {string} reason - Cancellation reason
   */
  cancelOrder: (orderId, reason) => {
    return api.post(`/api/ecommerce/orders/${orderId}/cancel/`, { reason });
  },

  /**
   * Track order
   * @param {number} orderId - Order ID
   */
  trackOrder: (orderId) => {
    return api.get(`/api/ecommerce/orders/${orderId}/track/`);
  },

  /**
   * Download the order invoice PDF.
   * Goes through `api` (not window.open) so the JWT and tenant headers are sent.
   * @param {number} orderId - Order ID
   */
  downloadOrderInvoice: (orderId) => {
    return api.get(`/api/ecommerce/orders/${orderId}/invoice/`, {
      responseType: 'blob',
    });
  },

  // ==================== CUSTOMER ====================

  /**
   * Get customer profile
   */
  getCustomerProfile: () => {
    return api.get('/api/ecommerce/customers/me/');
  },

  /**
   * Update customer profile
   * @param {Object} profileData - Profile update data
   */
  updateCustomerProfile: (profileData) => {
    return api.put('/api/ecommerce/customers/me/', profileData);
  },

  /**
   * Get customer statistics
   */
  getCustomerStatistics: () => {
    return api.get('/api/ecommerce/customers/me/statistics/');
  },

  /**
   * Change customer password
   * @param {Object} passwordData - Current and new password
   */
  changePassword: (passwordData) => {
    return api.post('/api/ecommerce/customers/me/change-password/', passwordData);
  },

  /**
   * Get notification settings
   */
  getNotificationSettings: () => {
    return api.get('/api/ecommerce/customers/me/notification-settings/');
  },

  /**
   * Update notification settings
   * @param {Object} settings - Notification preferences
   */
  updateNotificationSettings: (settings) => {
    return api.put('/api/ecommerce/customers/me/notification-settings/', settings);
  },

  // ==================== ADDRESSES ====================

  /**
   * Get customer addresses
   */
  getAddresses: () => {
    return api.get('/api/ecommerce/addresses/');
  },

  /**
   * Get address details
   * @param {number} addressId - Address ID
   */
  getAddress: (addressId) => {
    return api.get(`/api/ecommerce/addresses/${addressId}/`);
  },

  /**
   * Create new address
   * @param {Object} addressData - Address data
   */
  createAddress: (addressData) => {
    return api.post('/api/ecommerce/addresses/create/', addressData);
  },

  /**
   * Update address
   * @param {number} addressId - Address ID
   * @param {Object} addressData - Updated address data
   */
  updateAddress: (addressId, addressData) => {
    return api.put(`/api/ecommerce/addresses/${addressId}/`, addressData);
  },

  /**
   * Delete address
   * @param {number} addressId - Address ID
   */
  deleteAddress: (addressId) => {
    return api.delete(`/api/ecommerce/addresses/${addressId}/`);
  },

  /**
   * Set default address
   * @param {number} addressId - Address ID
   */
  setDefaultAddress: (addressId) => {
    return api.post(`/api/ecommerce/addresses/${addressId}/set-default/`);
  },

  // ==================== PAYMENT ====================

  /**
   * Verify payment
   * @param {number} paymentId - Payment ID
   * @param {Object} gatewayResponse - Gateway response data
   */
  verifyPayment: (paymentId, gatewayResponse) => {
    return api.post('/api/ecommerce/payment/verify/', {
      payment_id: paymentId,
      gateway_response: gatewayResponse,
    });
  },

  // ==================== PROMOTIONS ====================

  /**
   * Validate coupon code
   * @param {string} code - Coupon code
   * @param {number} cartTotal - Cart total amount
   */
  validateCoupon: (code, cartTotal) => {
    return api.post('/api/ecommerce/coupons/validate/', {
      code,
      cart_total: cartTotal,
    });
  },

  // ==================== WISHLISTS ====================

  /**
   * Get customer wishlists
   */
  getWishlists: () => {
    return api.get('/api/ecommerce/wishlists/');
  },

  /**
   * Get wishlist details
   * @param {number} wishlistId - Wishlist ID
   */
  getWishlist: (wishlistId) => {
    return api.get(`/api/ecommerce/wishlists/${wishlistId}/`);
  },

  /**
   * Create a new wishlist
   * @param {Object} data - Wishlist data (name, is_public)
   */
  createWishlist: (data) => {
    return api.post('/api/ecommerce/wishlists/', data);
  },

  /**
   * Update wishlist
   * @param {number} wishlistId - Wishlist ID
   * @param {Object} data - Updated data
   */
  updateWishlist: (wishlistId, data) => {
    return api.patch(`/api/ecommerce/wishlists/${wishlistId}/`, data);
  },

  /**
   * Delete wishlist
   * @param {number} wishlistId - Wishlist ID
   */
  deleteWishlist: (wishlistId) => {
    return api.delete(`/api/ecommerce/wishlists/${wishlistId}/`);
  },

  /**
   * Add item to wishlist
   * @param {number} wishlistId - Wishlist ID
   * @param {Object} data - Item data (web_product_id, variant_id, notes, priority)
   */
  addToWishlist: (wishlistId, data) => {
    return api.post(`/api/ecommerce/wishlists/${wishlistId}/add_item/`, data);
  },

  /**
   * Remove item from wishlist
   * @param {number} wishlistId - Wishlist ID
   * @param {number} itemId - Item ID
   */
  removeFromWishlist: (wishlistId, itemId) => {
    return api.post(`/api/ecommerce/wishlists/${wishlistId}/remove_item/`, { item_id: itemId });
  },

  /**
   * Move item from wishlist to cart
   * @param {number} wishlistId - Wishlist ID
   * @param {number} itemId - Item ID
   * @param {number} quantity - Quantity to add to cart
   */
  moveToCart: (wishlistId, itemId, quantity = 1) => {
    return api.post(`/api/ecommerce/wishlists/${wishlistId}/move_to_cart/`, {
      item_id: itemId,
      quantity,
    });
  },

  /**
   * Quick add product to default wishlist
   * @param {number} webProductId - Web product ID
   * @param {number} variantId - Optional variant ID
   */
  quickAddToWishlist: (webProductId, variantId = null) => {
    const data = { web_product_id: webProductId };
    if (variantId) data.variant_id = variantId;
    return api.post('/api/ecommerce/wishlist/quick-add/', data);
  },

  /**
   * Quick remove product from wishlist
   * @param {number} webProductId - Web product ID
   */
  quickRemoveFromWishlist: (webProductId) => {
    return api.post('/api/ecommerce/wishlist/quick-remove/', { web_product_id: webProductId });
  },

  /**
   * Check if product is in wishlist
   * @param {number} webProductId - Web product ID
   */
  checkInWishlist: (webProductId) => {
    return api.get('/api/ecommerce/wishlist/check/', { params: { web_product_id: webProductId } });
  },

  /**
   * Get shared wishlist by token
   * @param {string} shareToken - Share token
   */
  getSharedWishlist: (shareToken) => {
    return api.get(`/api/ecommerce/wishlist/shared/${shareToken}/`);
  },

  // ==================== PRODUCT REVIEWS ====================

  /**
   * Get reviews for a product
   * @param {Object} params - Query params (web_product, ordering, etc.)
   */
  getProductReviews: (params = {}) => {
    return api.get('/api/ecommerce/reviews/', { params });
  },

  /**
   * Get single review
   * @param {number} reviewId - Review ID
   */
  getReview: (reviewId) => {
    return api.get(`/api/ecommerce/reviews/${reviewId}/`);
  },

  /**
   * Create a product review
   * @param {Object} data - Review data (web_product, rating, title, review_text, etc.)
   */
  createReview: (data) => {
    return api.post('/api/ecommerce/reviews/', data);
  },

  /**
   * Update a review
   * @param {number} reviewId - Review ID
   * @param {Object} data - Updated data
   */
  updateReview: (reviewId, data) => {
    return api.patch(`/api/ecommerce/reviews/${reviewId}/`, data);
  },

  /**
   * Delete a review
   * @param {number} reviewId - Review ID
   */
  deleteReview: (reviewId) => {
    return api.delete(`/api/ecommerce/reviews/${reviewId}/`);
  },

  /**
   * Vote on a review's helpfulness
   * @param {number} reviewId - Review ID
   * @param {boolean} isHelpful - True for helpful, false for unhelpful
   */
  voteReview: (reviewId, isHelpful) => {
    return api.post(`/api/ecommerce/reviews/${reviewId}/vote/`, { is_helpful: isHelpful });
  },

  /**
   * Get review stats for a product
   * @param {number} reviewId - Review ID (to get product stats)
   */
  getReviewStats: (reviewId) => {
    return api.get(`/api/ecommerce/reviews/${reviewId}/stats/`);
  },

  /**
   * Get featured reviews
   */
  getFeaturedReviews: () => {
    return api.get('/api/ecommerce/reviews/featured/');
  },

  /**
   * Get recent reviews
   * @param {number} limit - Number of reviews to return
   */
  getRecentReviews: (limit = 10) => {
    return api.get('/api/ecommerce/reviews/recent/', { params: { limit } });
  },

  // ==================== SEARCH ====================

  /**
   * Instant search (autocomplete + quick results)
   * @param {string} query - Search query
   */
  instantSearch: (query) => {
    return api.get('/api/ecommerce/search/instant/', { params: { q: query } });
  },

  /**
   * Full search with facets
   * @param {Object} params - Search parameters
   */
  fullSearch: (params) => {
    return api.get('/api/ecommerce/search/', { params });
  },

  /**
   * Get popular searches
   */
  getPopularSearches: () => {
    return api.get('/api/ecommerce/search/popular/');
  },

  /**
   * Get trending searches
   */
  getTrendingSearches: () => {
    return api.get('/api/ecommerce/search/trending/');
  },

  /**
   * Get user search history
   */
  getSearchHistory: () => {
    return api.get('/api/ecommerce/search/history/');
  },

  /**
   * Clear search history
   */
  clearSearchHistory: () => {
    return api.post('/api/ecommerce/search/history/clear/');
  },

  // ==================== RECENTLY VIEWED ====================

  /**
   * Get recently viewed products for current user/session
   * @param {number} limit - Number of products to return (default 10)
   */
  getRecentlyViewed: (limit = 10) => {
    return api.get('/api/ecommerce/recently-viewed/', { params: { limit } });
  },

  /**
   * Track a product view
   * @param {Object} data - View tracking data (web_product_id, variant_id, device_type, referrer)
   */
  trackProductView: (data) => {
    return api.post('/api/ecommerce/recently-viewed/track/', data);
  },

  /**
   * Merge anonymous session views to customer account
   * @param {string} sessionKey - Session key to merge from
   */
  mergeRecentlyViewedSession: (sessionKey) => {
    return api.post('/api/ecommerce/recently-viewed/merge_session/', { session_key: sessionKey });
  },

  /**
   * Clear all recently viewed products
   */
  clearRecentlyViewed: () => {
    return api.delete('/api/ecommerce/recently-viewed/clear/');
  },

  /**
   * Remove a specific recently viewed product
   * @param {number} recentlyViewedId - Recently viewed product ID
   */
  removeRecentlyViewed: (recentlyViewedId) => {
    return api.delete(`/api/ecommerce/recently-viewed/${recentlyViewedId}/remove/`);
  },

  // ==================== PRODUCT COMPARISON ====================

  /**
   * Get current comparison list
   */
  getComparison: () => {
    return api.get('/api/ecommerce/comparison/current/');
  },

  /**
   * Get comparison matrix (features side-by-side)
   */
  getComparisonMatrix: () => {
    return api.get('/api/ecommerce/comparison/matrix/');
  },

  /**
   * Add product to comparison
   * @param {Object} data - Product data (web_product_id, variant_id, notes)
   */
  addToComparison: (data) => {
    return api.post('/api/ecommerce/comparison/add_item/', data);
  },

  /**
   * Remove product from comparison
   * @param {number} comparisonItemId - Comparison item ID
   */
  removeFromComparison: (comparisonItemId) => {
    return api.post('/api/ecommerce/comparison/remove_item/', { comparison_item_id: comparisonItemId });
  },

  /**
   * Clear all items from comparison
   */
  clearComparison: () => {
    return api.delete('/api/ecommerce/comparison/clear/');
  },

  /**
   * Add comparison item to cart
   * @param {number} comparisonItemId - Comparison item ID
   * @param {number} quantity - Quantity to add
   */
  addComparisonToCart: (comparisonItemId, quantity = 1) => {
    return api.post('/api/ecommerce/comparison/add_to_cart/', {
      comparison_item_id: comparisonItemId,
      quantity,
    });
  },

  /**
   * Merge anonymous session comparison to customer account
   * @param {string} sessionKey - Session key to merge from
   */
  mergeComparisonSession: (sessionKey) => {
    return api.post('/api/ecommerce/comparison/merge_session/', { session_key: sessionKey });
  },
};

export { ecommerceService };
export default ecommerceService;
